import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View } from '@smarterservices/smarter-ui';
import InPlaceEdit from '../../Common/InPlaceEdit';
import { getStore } from '../../../stores/StoreContext';

const ReviewerComments = forwardRef(({ annotation }, ref) => {
  const [comment, setComment] = useState(annotation?.comment || '');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const deleteButtonRef = useRef(null);
  const inPlaceEditRef = useRef(null);
  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');

  // Expose methods to parent components through ref
  useImperativeHandle(ref, () => ({
    startEditing: () => {
      if (inPlaceEditRef.current) {
        inPlaceEditRef.current.startEditing();
      }
    },
    focus: () => {
      if (inPlaceEditRef.current) {
        inPlaceEditRef.current.startEditing();
      }
    }
  }), []);

  // Focus the delete button when deleteConfirm becomes true
  useEffect(() => {
    if (deleteConfirm && deleteButtonRef.current) {
      deleteButtonRef.current.focus();
    }
  }, [deleteConfirm]);

  // Update comment state when annotation changes
  useEffect(() => {
    setComment(annotation?.reviewerComment || '');
  }, [annotation]);

  // Handle comment changes
  const handleCommentChange = async ({ oldText, newText }) => {
    try {
      // Update local state immediately for better UX
      setComment(newText);

      const updateResult = await ExamSessionStore.updateAnnotation(
        annotation.sid,
        {
          reviewerComment: newText,
          updatedBy: AppStore.user.username,
          updatedDate: new Date().toISOString(),
        }
      );
      if (!updateResult.successful) {
        return { success: false, message: 'Failed to update comment' };
      }

      // For now, we'll simulate a successful update
      return { success: true };
    } catch (error) {
      // If there's an error, return it to display in the UI
      console.log(error);
      return {
        success: false,
        message: error.message || 'Failed to update comment',
      };
    }
  };

  return (
    <View
      as="div"
      withVisualDebug={false}
      borderRadius="small"
      borderColor="primary"
      borderWidth="small none none none"
      background="primary"
      position="relative"
    >
      <InPlaceEdit
        ref={inPlaceEditRef}
        text={comment}
        placeholder="Reviewer Comment..."
        onChange={handleCommentChange}
      />
    </View>
  );
});

ReviewerComments.displayName = 'ReviewerComments';

export default ReviewerComments;
